/* eslint-disable  @typescript-eslint/no-explicit-any */
import axios, { AxiosResponse } from "axios";
import { AppResponse } from "../models/Response";
import { Util } from "../Util";
import { Admin } from "../models/Admin";
import { AddressModel } from "models/City";
import { OtpType } from "enum/OtpType";
import { UserTypes } from "enum/UserTypes";
import { User } from "models/User";

export interface UserLoginData {
  emailOrUserName: string;
  password: string;
  termsAndConditions: boolean;
}
export interface EmailOTPVerificationData {
  email: string;
  otpCode: string;
  otpType?: OtpType;
}

export interface ResetVerificationData {
  email: string;
  otpCode: string;
  password: string;
}

export interface ForgetPasswordEmailData {
  email: string;
}

export interface UserSignUpData {
  firstName: string;
  lastName: string;
  userName?: string;
  address: AddressModel;
  email: string;
  phoneNumber: string;
  password: string;
  isABusiness?: boolean;
  businessName?: string;
  role?: UserTypes,
  unitNo?: string;
}

export interface UserUpdateData {
  email: string;
}

export interface UserReset {
  userId: string;
  token: string;
  password: string;
}

export class AuthService {
  private static readonly TOKEN_KEY = "token";
  /** Kept when a merchant onboard/login temporarily replaces a staff session. */
  private static readonly STAFF_TOKEN_KEY = "zuluStaffToken";
  private static readonly STAFF_ROLES = new Set<string>([
    UserTypes.SUPER_ADMIN,
    UserTypes.DISPATCH,
  ]);

  private static extractAccessToken(res: any): string | null {
    const candidates = [res?.data, res?.token];
    for (const candidate of candidates) {
      if (typeof candidate === "string") {
        const token = candidate.trim();
        if (token.split(".").length === 3) {
          return token;
        }
      }
    }
    return null;
  }

  private static roleFromToken(token: string | null): string | null {
    if (!token) {
      return null;
    }
    const trimmed = token.trim();
    if (trimmed.split(".").length !== 3) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(trimmed.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      const role = payload?.user_role || payload?.role;
      return typeof role === "string" ? role : null;
    } catch {
      return null;
    }
  }

  private static isStaffRole(role: string | null | undefined): boolean {
    return Boolean(role && AuthService.STAFF_ROLES.has(role));
  }

  /**
   * Writes the active JWT. If a Zulu admin/dispatch session is about to be
   * replaced by a merchant customer JWT (Shopify/Woo onboard in the same
   * browser), the staff token is stashed so it can be restored afterward.
   */
  private static writeActiveToken(token: string): void {
    const next = token.trim();
    if (!next || next.split(".").length !== 3) {
      return;
    }
    const current = localStorage.getItem(AuthService.TOKEN_KEY);
    const currentRole = AuthService.roleFromToken(current);
    const nextRole = AuthService.roleFromToken(next);
    if (
      current &&
      AuthService.isStaffRole(currentRole) &&
      nextRole &&
      !AuthService.isStaffRole(nextRole)
    ) {
      localStorage.setItem(AuthService.STAFF_TOKEN_KEY, current);
    }
    if (AuthService.isStaffRole(nextRole)) {
      localStorage.removeItem(AuthService.STAFF_TOKEN_KEY);
    }
    localStorage.setItem(AuthService.TOKEN_KEY, next);
  }

  /** Puts a stashed admin/dispatch JWT back as the active session. */
  public static restoreStaffSession(): boolean {
    const staff = localStorage.getItem(AuthService.STAFF_TOKEN_KEY);
    if (!staff || !AuthService.isStaffRole(AuthService.roleFromToken(staff))) {
      return false;
    }
    localStorage.setItem(AuthService.TOKEN_KEY, staff);
    localStorage.removeItem(AuthService.STAFF_TOKEN_KEY);
    return true;
  }

  public static clearStaffSessionStash(): void {
    localStorage.removeItem(AuthService.STAFF_TOKEN_KEY);
  }

  public static hasStashedStaffSession(): boolean {
    const staff = localStorage.getItem(AuthService.STAFF_TOKEN_KEY);
    return Boolean(staff && AuthService.isStaffRole(AuthService.roleFromToken(staff)));
  }

  public static async getMe(): Promise<AppResponse<User>> {
    const ep = Util.apiAuthUrl("me");
    const res = await axios.get<void, AppResponse<Admin>>(ep);
    if (res.error) {
     // localStorage.removeItem(AuthService.TOKEN_KEY);
    }
    return res;
  }

  public static async userLogin(userLoginData: UserLoginData): Promise<AppResponse<string>> {
    const ep = Util.apiPublicUrl("userlogin");
    const res = await axios.post<UserLoginData, AppResponse<string>>(ep, userLoginData);
    const accessToken = AuthService.extractAccessToken(res);
    if (res.success && accessToken) {
      AuthService.writeActiveToken(accessToken);
      res.data = accessToken;
    }
    if (userLoginData.termsAndConditions) {
      localStorage.setItem("remember", "Yes");
    } else {
      {
      localStorage.setItem("remember", "");
      }
    }
    return res;
  }

  public static async userSignup(userSignupData: UserSignUpData): Promise<AppResponse<string>> {
    const ep = Util.apiPublicUrl("signup");
    const res = await axios.post<UserLoginData, AppResponse<string>>(ep, userSignupData);
    if (res.success) {
      localStorage.setItem("data", JSON.stringify(userSignupData));
    }
    return res;
  }

  public static async emailOTPVerification(emailOTPVerificationData: EmailOTPVerificationData): Promise<AppResponse<string>> {
    const ep = Util.apiPublicUrl("verify");
    const res = await axios.post<EmailOTPVerificationData, AppResponse<string>>(ep, emailOTPVerificationData);
    return res;
  }

  public static async resetVerification(resetVerificationData: ResetVerificationData): Promise<AppResponse<string>> {
    const ep = Util.apiPublicUrl("password/reset");
    const res = await axios.post<EmailOTPVerificationData, AppResponse<string>>(ep, resetVerificationData);
    return res;
  }

  public static async forgetPasswordEmail(forgetPasswordEmailData: ForgetPasswordEmailData): Promise<AppResponse<string>> {
    const ep = Util.apiPublicUrl("password/recover");
    const res = await axios.post<ForgetPasswordEmailData, AppResponse<string>>(ep, forgetPasswordEmailData);
    return res;
  }

  public static async forgetPasswordOTPVerification(emailOTPVerificationData: EmailOTPVerificationData): Promise<AppResponse<string>> {
    const ep = Util.apiPublicUrl("verify-otp");
    const res = await axios.post<EmailOTPVerificationData, AppResponse<string>>(ep, emailOTPVerificationData);
    return res;
  }

  public static userLogout(): void {
    localStorage.removeItem(AuthService.TOKEN_KEY);
    // Merchant onboard in an admin browser: drop the merchant JWT and put
    // the stashed staff session back instead of wiping both.
    if (AuthService.restoreStaffSession()) {
      return;
    }
    AuthService.clearStaffSessionStash();
  }

  public static getToken(): string | null {
    return localStorage.getItem(AuthService.TOKEN_KEY);
  }

  public static setToken(token: string): void {
    AuthService.writeActiveToken(token);
  }

  public static async sendOnboardingOtp(): Promise<AppResponse<{ email: string }>> {
    const ep = Util.apiAuthUrl("user/onboarding-otp");
    return axios.post<void, AppResponse<{ email: string }>>(ep);
  }

  public static async resendOtp(email: Partial<EmailOTPVerificationData>): Promise<AppResponse<null>> {
    const ep = Util.apiPublicUrl("resend/otp");
    const res = await axios.post<string, AppResponse<null>>(ep, email);
    return res;
  }

  public static async updateOperatingHours(data: User): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("user/operating-hours");
    const res = await axios.post<User, AppResponse<string>>(ep, data);
    return res;
  }

  public static async comparePassword(data: any): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("user/compare-password");
    const res = await axios.post<void, AppResponse<string>>(ep, data);
    return res;
  }

  public static async changePassword(data: any): Promise<AppResponse<string>> {
    const ep = Util.apiAuthUrl("user/password");
    const res = await axios.post<void, AppResponse<string>>(ep, data);
    return res;
  }

  public static async uploadImage(data: any): Promise<AppResponse<User>> {
    const ep = Util.apiAuthUrl("user/upload-image");
    const res = await axios.post<any, AppResponse<User>>(ep, data, { headers: { "Content-Type": "multipart/form-data" } });
    return res;
  }

  public static async linkShopifyStore(session: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("integrations/shopify/link");
    return axios.post<{ session: string }, AppResponse<any>>(ep, { session });
  }

  public static async linkWooCommerceStore(session: string): Promise<AppResponse<any>> {
    const ep = Util.apiAuthUrl("integrations/woocommerce/link");
    return axios.post<{ session: string }, AppResponse<any>>(ep, { session });
  }

  // public static async getProfileImage(imageId?: string): Promise<AppResponse<string>> {
  //   const ep = Util.apiAuthUrl(`user/image/` + imageId);
  //   const res = await axios.get<void, AppResponse<string>>(ep);
  //   return res;
  // }
 public static async getProfileImage(imageId: string): Promise<AxiosResponse<ArrayBuffer>> {
  if (!imageId?.trim()) {
    throw new Error('Image ID is required and cannot be empty');
  }
  const endpoint = Util.apiPublicUrl(`user/image/` + imageId);
  const response = await axios.get(endpoint, {
    responseType: 'arraybuffer'
  });
  return response; 
}
}
